import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PreviewMap from './PreviewMap';
import CornerThumb from './CornerThumb';
import { usePreviewMedia } from '../hooks';

// Photo thumb position when map is shown
// to no cover the map controls
const PHOTO_POSITION_RIGHT = 80;
const PHOTO_POSITION_BOTTOM = 50;
const CORNERTHUMB_POSITION = 20;

export default function Carousel() {
  const photos = usePreviewMedia();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showingMap, setShowingMap] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mapAsItem = isMobile;
  const prevIsMobile = useRef(isMobile);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const hasPhotos = photos.length > 0;
  const totalCount = mapAsItem ? photos.length + 1 : photos.length;
  const isViewingMap = mapAsItem
    ? currentIndex === 0
    : showingMap || !hasPhotos;

  // Sync state when resizing between mobile and desktop
  useEffect(() => {
    if (prevIsMobile.current !== isMobile) {
      if (isMobile) {
        // Desktop to Mobile
        if (showingMap) {
          setCurrentIndex(0); // View Map
        } else {
          setCurrentIndex(currentIndex + 1); // View Photo
        }
      } else {
        // Mobile to Desktop
        if (currentIndex === 0) {
          setShowingMap(true);
          setCurrentIndex(0);
        } else {
          setShowingMap(false);
          setCurrentIndex(currentIndex - 1);
        }
      }
      prevIsMobile.current = isMobile;
    }
  }, [isMobile, showingMap, currentIndex]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const activeChild = scrollContainerRef.current.children[
        currentIndex
      ] as HTMLElement;
      if (activeChild) {
        activeChild.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [currentIndex]);

  const goTo = useCallback(
    (index: number) => {
      if (totalCount === 0) return;
      const next = (index + totalCount) % totalCount;
      setCurrentIndex(next);
      if (!mapAsItem && showingMap) setShowingMap(false);
    },
    [showingMap, totalCount, mapAsItem],
  );

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    goTo(currentIndex - 1);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    goTo(currentIndex + 1);
  };

  const toggleMap = () => {
    if (mapAsItem) {
      goTo(0);
    } else {
      setShowingMap((v) => !v);
    }
  };

  const currentPhotoIndex = mapAsItem ? currentIndex - 1 : currentIndex;
  const currentPhoto =
    hasPhotos && currentPhotoIndex >= 0 && currentPhotoIndex < photos.length
      ? photos[currentPhotoIndex]
      : null;
  const currentSrc = currentPhoto
    ? currentPhoto.MEDIA_URL || currentPhoto.MED_URL
    : '';

  const thumbHeight = { xs: 45, md: 75, lg: 105 };
  const thumbWidth = { xs: 65, md: 120, lg: 180 };

  return (
    <Box sx={{ maxWidth: 1, width: '100%', mx: 'auto', py: 4 }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: isViewingMap ? 500 : 'auto',
          aspectRatio: isViewingMap ? 'auto' : '16/9',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#111',
          transition: 'height 0.3s ease-in-out',
        }}
      >
        {isViewingMap || !hasPhotos ? (
          <Box sx={{ height: 500 }}>
            <PreviewMap height="100%" />
          </Box>
        ) : (
          <Box
            component="img"
            src={currentSrc}
            alt={`Property photo ${mapAsItem ? currentIndex : currentIndex + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'opacity 0.25s',
            }}
          />
        )}

        {hasPhotos && !mapAsItem && (
          <CornerThumb
            onToggle={toggleMap}
            height={thumbHeight}
            width={thumbWidth}
            bottom={
              isViewingMap
                ? CORNERTHUMB_POSITION + PHOTO_POSITION_BOTTOM
                : CORNERTHUMB_POSITION
            }
            right={
              isViewingMap
                ? CORNERTHUMB_POSITION + PHOTO_POSITION_RIGHT
                : CORNERTHUMB_POSITION
            }
          >
            {isViewingMap ? (
              <Box
                component="img"
                src={currentSrc}
                alt="Property photo"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                sx={{ width: '100%', height: '100%', pointerEvents: 'none' }}
              >
                <PreviewMap height="100%" />
              </Box>
            )}
          </CornerThumb>
        )}

        {hasPhotos && (!showingMap || mapAsItem) && (
          <Box
            sx={{
              position: 'absolute',
              top: 14,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.45)',
              color: '#fff',
              fontSize: 12,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              userSelect: 'none',
              zIndex: 5,
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: 14 }} />
            {currentIndex + 1} of {totalCount}
          </Box>
        )}

        {hasPhotos && (!showingMap || mapAsItem) && totalCount > 1 && (
          <>
            <IconButton
              onClick={handlePrev}
              aria-label="Previous photo"
              sx={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'white' },
                width: 34,
                height: 34,
                zIndex: 5,
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={handleNext}
              aria-label="Next photo"
              sx={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'white' },
                width: 34,
                height: 34,
                zIndex: 5,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Box>

      {!hasPhotos && (
        <Box
          sx={{
            height: 85,
            width: 135,
            bgcolor: 'grey.100',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            mt: 1,
            mb: 3,
            mx: 'auto',
          }}
        >
          <PhotoCameraOutlinedIcon
            sx={{ fontSize: 24, color: 'grey.400', mb: 1 }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            No Photo Available
          </Typography>
        </Box>
      )}

      {hasPhotos && (
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 1,
            mt: 1.25,
            overflowX: 'auto',
            pb: 0.5,
            '&::before, &::after': {
              content: '""',
              flex: 1,
            },
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'divider',
              borderRadius: 2,
            },
          }}
        >
          {mapAsItem && (
            <Box
              key="map-thumb"
              onClick={() => goTo(0)}
              sx={{
                width: 110,
                height: 74,
                flexShrink: 0,
                borderRadius: 1.5,
                overflow: 'hidden',
                cursor: 'pointer',
                border: '2px solid',
                borderColor: isViewingMap ? 'primary.main' : 'transparent',
                transition: 'border-color 0.2s',
              }}
            >
              <Box
                sx={{ width: '100%', height: '100%', pointerEvents: 'none' }}
              >
                <PreviewMap height="100%" />
              </Box>
            </Box>
          )}
          {photos.map((photo, i) => {
            const thumbSrc = photo.MEDIA_URL || photo.MED_URL;
            const itemIndex = mapAsItem ? i + 1 : i;
            return (
              <Box
                key={i}
                onClick={() => goTo(itemIndex)}
                sx={{
                  width: 110,
                  height: 74,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor:
                    !isViewingMap && itemIndex === currentIndex
                      ? 'primary.main'
                      : 'transparent',
                  transition: 'border-color 0.2s',
                }}
              >
                <Box
                  component="img"
                  src={thumbSrc}
                  alt={`Thumbnail ${i + 1}`}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
